import { Link, NavLink } from 'react-router'
import { Icon } from '../components/index.js'
import { useAuth } from '../lib/auth.js'
import { useHealth, useSchema } from '../lib/hooks.js'
import { useTheme } from './ThemeProvider.js'
import { BrandMark } from '../screens/signin/BrandMark.js'
import { CommandPaletteButton } from './CommandPaletteButton.js'

export interface NavItem {
  to: string
  label: string
  icon: string
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/** Two groups, as in the Nocturne 2 design: the day-to-day work, then the things you build and run. */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { to: '/', label: 'Home', icon: 'House' },
      { to: '/content', label: 'Content', icon: 'Files' },
      { to: '/site-map', label: 'Site map', icon: 'Globe' },
      { to: '/media', label: 'Media', icon: 'Images' },
      { to: '/inbox', label: 'Inbox', icon: 'Tray' },
      { to: '/schedule', label: 'Schedule', icon: 'CalendarBlank' },
      { to: '/forms', label: 'Forms', icon: 'ListChecks' },
    ],
  },
  {
    label: 'Build',
    items: [
      { to: '/model', label: 'Content model', icon: 'TreeStructure' },
      { to: '/settings', label: 'Settings', icon: 'GearSix' },
      { to: '/updates', label: 'Updates', icon: 'ArrowsClockwise' },
    ],
  },
]

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

export function Sidebar({ onOpenPalette = () => {} }: { onOpenPalette?: () => void }) {
  const { user, signOut } = useAuth()
  const { theme, setTheme } = useTheme()
  const health = useHealth().data
  const organisation = health?.organisation
  // The project's own screens (core's sections.ts), a third group that only
  // appears once the pushed schema declares one.
  const sections = useSchema().data?.definition?.sections ?? []
  const groups: NavGroup[] =
    sections.length > 0
      ? [...NAV_GROUPS, { label: 'Your project', items: sections.map((section) => ({ to: `/sections/${section.key}`, label: section.name, icon: section.icon })) }]
      : NAV_GROUPS

  return (
    <nav aria-label="Main" className="stack lc-sidebar">
      {organisation?.logoUrl ? (
        // The organisation's own logo (Settings → Instance), shown as it is,
        // with its name and LoopCMS (mark and all) underneath.
        <div className="row" style={{ gap: 10, paddingLeft: 4, minWidth: 0 }}>
          <img className="lc-org-logo" src={organisation.logoUrl} alt={organisation.name ?? 'Organisation logo'} />
          <span className="stack" style={{ gap: 2, minWidth: 0 }}>
            {organisation.name ? (
              <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {organisation.name}
              </span>
            ) : null}
            <span className="row text-muted" style={{ gap: 5, fontSize: 11.5 }}>
              <BrandMark size={13} />
              <span>LoopCMS</span>
            </span>
          </span>
        </div>
      ) : (
        <div className="row" style={{ gap: 9, paddingLeft: 6 }}>
          <BrandMark size={26} />
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' }}>
            Loop <span className="text-muted" style={{ fontWeight: 400 }}>CMS</span>
          </span>
        </div>
      )}

      <CommandPaletteButton onOpen={onOpenPalette} variant="sidebar" />

      <div className="stack" style={{ gap: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
        {groups.map((group) => (
          <div key={group.label} className="stack" style={{ gap: 2 }}>
            <span className="lc-nav-label">{group.label}</span>
            {group.items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} className="lc-nav-item">
                <Icon name={item.icon} size={18} />
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      {health?.version ? (
        // The server's version, from the health check, so it's always what's
        // actually running rather than what this bundle was built as.
        <span className="lc-sidebar-version">LoopCMS {health.version}</span>
      ) : null}

      <div className="lc-user-card">
        <Link to="/account" aria-label="Your account" className="row" style={{ gap: 10, flex: 1, minWidth: 0, textDecoration: 'none', color: 'inherit' }}>
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" style={{ width: 28, height: 28, flex: 'none', borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <span aria-hidden className="lc-avatar">{user?.name.slice(0, 1)}</span>
          )}
          <span className="stack" style={{ gap: 0, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</span>
            <span className="text-muted" style={{ fontSize: 11.5 }}>{user?.role}</span>
          </span>
        </Link>
        <button
          type="button"
          className="btn btn-icon"
          aria-label={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        >
          <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={16} />
        </button>
        <button type="button" className="btn btn-icon" aria-label="Sign out" title="Sign out" onClick={() => void signOut()}>
          <Icon name="SignOut" size={16} />
        </button>
      </div>
    </nav>
  )
}
